import React from "react";
import styles from "./followup.module.css";

type FollowUpQuestionsProps = {
  content: string;
  onQuestionClick?: (question: string) => void;
  disabled?: boolean;
};

const FollowUpQuestions: React.FC<FollowUpQuestionsProps> = ({
  content,
  onQuestionClick,
  disabled = false,
}) => {
  // Parse the content to extract questions, excluding headers
  const parseQuestions = (text: string): string[] => {
    const lines = text.split("\n").filter((line) => line.trim());
    const questions: string[] = [];

    lines.forEach((line) => {
      const trimmed = line.trim();

      // Skip empty lines or very short lines
      if (trimmed.length < 5) {
        return;
      }

      // Skip markdown headers (lines starting with #)
      if (trimmed.startsWith("#")) {
        return;
      }

      // Skip markdown table separators (lines with |---|---|)
      if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
        return;
      }

      // Parse markdown table rows: | 1 | Question text | Category |
      const tableMatch = trimmed.match(
        /^\|\s*\d+\s*\|\s*(.+?)\s*\|\s*[^|]+\s*\|$/
      );
      if (tableMatch && tableMatch[1]) {
        const question = tableMatch[1].trim();
        if (question.length > 5 && !question.match(/^[#\-=_*|]+$/)) {
          questions.push(question);
        }
        return;
      }

      // Parse markdown list items (-, *, 1., etc.)
      const listMatch =
        line.match(/^[\s]*[-*][\s]+(.+)$/) ||
        line.match(/^[\s]*\d+\.[\s]+(.+)$/);

      if (listMatch && listMatch[1]) {
        const question = listMatch[1].trim();
        // Remove trailing category markers like | Category |
        const cleanQuestion = question
          .replace(/\s*\|\s*[^|]+\s*\|?\s*$/g, "")
          .trim();
        if (cleanQuestion.length > 5) {
          questions.push(cleanQuestion);
        }
      }
    });

    return questions;
  };

  const questions = parseQuestions(content);

  if (questions.length === 0) {
    return (
      <div className={styles.noQuestions}>No follow-up questions available</div>
    );
  }

  return (
    <div className={styles.followUpContainer}>
      <div className={styles.questionList}>
        {questions.map((question, index) => (
          <button
            key={index}
            className={styles.questionButton}
            onClick={() => onQuestionClick?.(question)}
            disabled={disabled}
            title={
              disabled
                ? "Please wait for the current response"
                : "Click to ask this question"
            }
          >
            <span className={styles.questionText}>{question}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default FollowUpQuestions;
